/**
 * AdminMagicLinkCard — admin generates one-time invite links for
 * vendors / professionals. Phone is the only input. The card returns
 * a shareable URL and a "Send via WhatsApp" button that pre-fills a
 * message at wa.me/<phone>.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LinkSimple, Copy, WhatsappLogo, Trash, Spinner, ArrowsClockwise, CaretDown, CaretUp,
} from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { VENDOR_CATEGORIES, PROFESSIONAL_GROUPS, ENTERTAINMENT_GROUP } from '../data/registerData';

// Subset of vendor categories where a "registered under government"
// checkbox decides whether the recipient is saved as a vendor (ticked)
// vs as a professional (unticked). Caterer↔catering-team and
// decorator↔decoration-team are equivalent buckets across roles.
const GOV_GATED_VENDOR_CATEGORIES = new Set([
  'photographer', 'caterer', 'decorator',
]);

// Map a gov-unticked vendor-style category to its professional twin so
// the recipient lands on the right form. E.g. selecting "Caterer" then
// unticking the checkbox means they're effectively a "Catering Team"
// professional.
const VENDOR_TO_PRO_CATEGORY = {
  caterer: 'catering_team',
  decorator: 'decoration_team',
  photographer: 'photographer_pro',
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Normalise phone to digits-only with country code so wa.me works reliably.
const waPhone = (p) => (p || '').replace(/\D+/g, '');

// "2 hours ago" / "3 days ago"
const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const StatusPill = ({ status }) => {
  const map = {
    active:  'bg-green-100 text-green-700 border-green-300',
    used:    'bg-blue-100 text-blue-700 border-blue-300',
    expired: 'bg-gray-100 text-gray-600 border-gray-300',
  };
  return (
    <span
      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${map[status] || map.expired}`}
      data-testid={`magic-link-status-${status}`}
    >
      {status}
    </span>
  );
};

export default function AdminMagicLinkCard() {
  const [phone, setPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  // Pre-fill data (all optional). When set, the recipient's MagicLinkRegisterPage
  // pre-populates and they only confirm — same code path, no new branch.
  const [showPrefill, setShowPrefill] = useState(false);
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [govRegistered, setGovRegistered] = useState(false);

  // Derive whether the chosen category is in the gov-gated vendor bucket.
  const isGovGatedCategory = GOV_GATED_VENDOR_CATEGORIES.has(category);
  // Final (sent) role + category resolved by the gov checkbox rule.
  let resolvedRole = '';
  let resolvedCategory = category;
  if (category) {
    const isVendorCat = VENDOR_CATEGORIES.some((c) => c.value === category);
    if (isGovGatedCategory) {
      if (govRegistered) { resolvedRole = 'vendor'; resolvedCategory = category; }
      else { resolvedRole = 'professional'; resolvedCategory = VENDOR_TO_PRO_CATEGORY[category] || category; }
    } else if (isVendorCat) {
      resolvedRole = 'vendor';
    } else {
      resolvedRole = 'professional';
    }
  }
  const isProfessional = resolvedRole === 'professional';
  // For professionals / entertainment-pros, name is mandatory.
  const nameRequiredButMissing = isProfessional && !name.trim();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/magic-links`,
        { withCredentials: true });
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    const p = phone.trim();
    if (!p) { toast.error('Enter a phone number'); return; }
    if (nameRequiredButMissing) {
      toast.error('Name is required for professionals / entertainment pros');
      return;
    }
    setCreating(true);
    try {
      const prefill = {};
      if (name.trim()) prefill.name = name.trim();
      if (businessName.trim()) prefill.business_name = businessName.trim();
      if (city.trim()) prefill.location = city.trim();
      if (resolvedCategory) prefill.category = resolvedCategory;
      if (resolvedRole) prefill.role = resolvedRole;
      if (isGovGatedCategory) prefill.business_registered = govRegistered;
      const payload = { phone: p };
      if (Object.keys(prefill).length) payload.prefill = prefill;

      const { data } = await axios.post(
        `${BACKEND_URL}/api/admin/magic-links`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Invite link created${resolvedRole ? ` (${resolvedRole})` : ''}`);
      setPhone(''); setName(''); setBusinessName(''); setCity(''); setCategory(''); setGovRegistered(false); setShowPrefill(false);
      setItems((prev) => [data, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create link');
    } finally {
      setCreating(false);
    }
  };

  const copy = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      // Older browsers — fall back to a temporary input element.
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); toast.success('Link copied'); }
      catch { toast.error('Copy failed — long-press the link manually'); }
      document.body.removeChild(ta);
    }
    setBusyId(id);
    setTimeout(() => setBusyId(null), 600);
  };

  const sendWhatsApp = (item) => {
    const msg = encodeURIComponent(
      `Hi! You're invited to list yourself on Beeweddy 🎉\n\n` +
      `Tap this private invite link to finish your profile in under a minute (no OTP needed):\n${item.url}\n\n` +
      `Link is valid for 7 days.`
    );
    const num = waPhone(item.phone);
    const url = num ? `https://wa.me/${num}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this invite link? The recipient won\'t be able to use it.')) return;
    setBusyId(id);
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/magic-links/${id}`,
        { withCredentials: true });
      toast.success('Link revoked');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card data-testid="admin-magic-link-card">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <LinkSimple size={20} weight="duotone" />
              Magic Invite Links
            </CardTitle>
            <CardDescription>
              Skip the OTP. Generate a 7-day single-use link, share it on WhatsApp,
              and the vendor / pro lands on a pre-filled signup form.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="magic-link-refresh-btn"
          >
            <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Create form */}
        <div className="border-2 border-dashed border-[#E8E1D5] rounded p-3 mb-4">
          <Label htmlFor="magic-link-phone">Phone (with country code)</Label>
          <div className="flex gap-2 mt-1">
            <Input
              id="magic-link-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 98765 43210"
              data-testid="magic-link-phone-input"
              onKeyDown={(e) => e.key === 'Enter' && create()}
            />
            <Button
              onClick={create}
              disabled={creating || !phone.trim()}
              data-testid="magic-link-create-btn"
            >
              {creating ? <Spinner size={16} className="animate-spin" /> : 'Generate'}
            </Button>
          </div>
          <p className="text-[11px] text-text-secondary mt-1">
            Recipient will see this phone locked on the signup page. No OTP needed.
          </p>

          {/* Optional pre-fill — admin types contact info / category here so
              the recipient's register form is already filled. Same form,
              same backend — just pre-populated. */}
          <button type="button" onClick={() => setShowPrefill((v) => !v)}
            className="mt-2 text-[12px] font-bold text-brand-primary inline-flex items-center gap-1"
            data-testid="magic-link-prefill-toggle">
            {showPrefill ? <CaretUp size={12} /> : <CaretDown size={12} />}
            {showPrefill ? 'Hide pre-fill (advanced)' : 'Add contact / category info (optional)'}
          </button>

          {showPrefill && (
            <div className="mt-3 space-y-3 border-t border-[#E8E1D5] pt-3" data-testid="magic-link-prefill-block">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ml-name" className="text-xs">
                    Name {isProfessional && <span className="text-rose-600">*</span>}
                  </Label>
                  <Input id="ml-name" value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Recipient's name"
                    data-testid="magic-link-name-input" />
                </div>
                <div>
                  <Label htmlFor="ml-biz" className="text-xs">Company / Business name</Label>
                  <Input id="ml-biz" value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g. Sundar Photography"
                    data-testid="magic-link-business-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ml-city" className="text-xs">City</Label>
                  <Input id="ml-city" value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Chennai"
                    data-testid="magic-link-city-input" />
                </div>
                <div>
                  <Label htmlFor="ml-cat" className="text-xs">Profession / Category</Label>
                  <select id="ml-cat" value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full h-9 border border-[#E8E1D5] rounded-none px-2 text-sm bg-white"
                    data-testid="magic-link-category-select">
                    <option value="">— Select —</option>
                    <optgroup label="Vendor / Business">
                      {VENDOR_CATEGORIES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </optgroup>
                    {PROFESSIONAL_GROUPS.map((g) => (
                      <optgroup key={g.label} label={`Professional — ${g.label}`}>
                        {(g.items || []).map((it) => (
                          <option key={it.value} value={it.value}>{it.label}</option>
                        ))}
                      </optgroup>
                    ))}
                    <optgroup label={`Professional — ${ENTERTAINMENT_GROUP.label}`}>
                      {(ENTERTAINMENT_GROUP.items || []).map((it) => (
                        <option key={it.value} value={it.value}>{it.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Government-registered checkbox — only meaningful for the
                  gov-gated vendor categories. Drives vendor vs. professional. */}
              {isGovGatedCategory && (
                <label className="flex items-start gap-2 cursor-pointer p-2 border border-[#E8E1D5] bg-stone-50"
                  data-testid="magic-link-gov-checkbox-row">
                  <input type="checkbox" checked={govRegistered}
                    onChange={(e) => setGovRegistered(e.target.checked)}
                    className="mt-0.5"
                    data-testid="magic-link-gov-checkbox" />
                  <span className="text-xs leading-snug">
                    My shop / business is <strong>registered under government</strong>
                    <br />
                    <span className="text-text-secondary">
                      Ticked → saved as <strong>Vendor</strong> · Unticked → saved as a <strong>Professional</strong> ({VENDOR_TO_PRO_CATEGORY[category] || category}).
                    </span>
                  </span>
                </label>
              )}

              {category && (
                <p className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 p-2 rounded"
                  data-testid="magic-link-resolved-role">
                  → Will be saved as <strong>{resolvedRole}</strong>, category <strong>{resolvedCategory}</strong>.
                  After signup they&apos;ll land on the {resolvedRole === 'vendor' ? 'service / package' : 'package / portfolio'} creation form for this category.
                </p>
              )}
            </div>
          )}
        </div>

        {/* List */}
        {loading && items.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner size={24} className="animate-spin text-[#8B5E34]" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-text-secondary py-6"
            data-testid="magic-link-empty">
            No invite links yet. Generate one above.
          </div>
        ) : (
          <div className="space-y-2" data-testid="magic-link-list">
            {items.map((it) => {
              const isActive = it.status === 'active';
              return (
                <div
                  key={it.id}
                  className={`border-2 rounded p-3 ${
                    isActive ? 'border-[#E8E1D5] bg-white' : 'border-gray-200 bg-gray-50'
                  }`}
                  data-testid={`magic-link-row-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-bold">{it.phone}</span>
                      <StatusPill status={it.status} />
                    </div>
                    <span className="text-[11px] text-text-secondary">
                      Created {fmtAgo(it.created_at)} · expires {fmtDate(it.expires_at)}
                    </span>
                  </div>

                  {isActive && (
                    <>
                      <div className="text-[11px] font-mono break-all bg-[#FAF6EE] p-2 rounded mb-2"
                        data-testid={`magic-link-url-${it.id}`}>
                        {it.url}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copy(it.url, it.id)}
                          disabled={busyId === it.id}
                          data-testid={`magic-link-copy-btn-${it.id}`}
                        >
                          <Copy size={14} className="mr-1" /> Copy link
                        </Button>
                        <Button
                          size="sm"
                          className="bg-[#25D366] hover:bg-[#1FB855] text-white"
                          onClick={() => sendWhatsApp(it)}
                          data-testid={`magic-link-whatsapp-btn-${it.id}`}
                        >
                          <WhatsappLogo size={14} weight="fill" className="mr-1" />
                          Send via WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => revoke(it.id)}
                          disabled={busyId === it.id}
                          data-testid={`magic-link-revoke-btn-${it.id}`}
                        >
                          <Trash size={14} className="mr-1" /> Revoke
                        </Button>
                      </div>
                    </>
                  )}

                  {it.status === 'used' && (
                    <p className="text-[11px] text-text-secondary">
                      ✓ Used {fmtAgo(it.used_at)} — account created.
                    </p>
                  )}
                  {it.status === 'expired' && (
                    <p className="text-[11px] text-text-secondary">
                      Link expired. Generate a fresh one if needed.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
